import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrosshairs, faMapLocationDot } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../../AxiosInstance";
import { toast, ToastTypes } from "../../../components/ajonjolib/toasts/toast/toast";
import styles from "../company.module.css";

// Sin centrado guardado: Perú entero.
const FALLBACK_VIEW = { lat: -9.19, lng: -75.02, zoom: 5 };

function savedView(company) {
  if (company.latitude === null || company.longitude === null || company.zoom === null) return null;
  return { lat: company.latitude, lng: company.longitude, zoom: company.zoom };
}

// El mapa de calor IMU abre en esta vista. Se elige moviendo el mapa: lo que
// queda bajo la mira del centro, con el zoom actual.
export default function MapCenterCard({ company, setData }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [view, setView] = useState(savedView(company) || FALLBACK_VIEW);
  const [saving, setSaving] = useState(false);
  const saved = savedView(company);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const start = savedView(company) || FALLBACK_VIEW;
    const map = L.map(containerRef.current).setView([start.lat, start.lng], start.zoom);
    L.tileLayer(process.env.REACT_APP_MAP_URL, { tileSize: 512, zoomOffset: -1 }).addTo(map);
    const sync = () => {
      const c = map.getCenter();
      setView({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
    };
    map.on("moveend zoomend", sync);
    mapRef.current = map;
    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
    };
    // Solo al montar: después manda lo que el usuario mueve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const round = (n) => Math.round(n * 1e5) / 1e5;
  const dirty =
    !saved || round(saved.lat) !== round(view.lat) || round(saved.lng) !== round(view.lng) || saved.zoom !== view.zoom;

  const save = () => {
    setSaving(true);
    axiosInstance
      .patch("company/settings/", {
        latitude: round(view.lat),
        longitude: round(view.lng),
        zoom: Math.min(Math.max(view.zoom, 1), 20),
      })
      .then((response) => {
        setData(response.data);
        toast(t("company.map.saved"), ToastTypes.SUCCESS);
      })
      .catch(() => toast(t("company.saveError"), ToastTypes.ERROR))
      .finally(() => setSaving(false));
  };

  const backToSaved = () => {
    if (saved && mapRef.current) mapRef.current.setView([saved.lat, saved.lng], saved.zoom);
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>
            <FontAwesomeIcon icon={faMapLocationDot} className={styles.cardTitleIcon} />
            {t("company.map.title")}
          </div>
          <div className={styles.cardHelp}>{t("company.map.help")}</div>
        </div>
      </div>

      <div className={styles.mapFrame}>
        <div ref={containerRef} className={styles.map} />
        <FontAwesomeIcon icon={faCrosshairs} className={styles.crosshair} />
      </div>
      <div className={styles.mapReadout}>
        <span>
          {t("company.map.center")} <strong>{view.lat.toFixed(5)}, {view.lng.toFixed(5)}</strong>
        </span>
        <span>
          {t("company.map.zoom")} <strong>{view.zoom}</strong>
        </span>
        {!saved && <span>{t("company.map.notSet")}</span>}
      </div>

      <div className={styles.actions}>
        {saved && dirty && (
          <button type="button" className={styles.ghostButton} onClick={backToSaved}>
            {t("company.map.backToSaved")}
          </button>
        )}
        <button type="button" className={styles.primaryButton} disabled={!dirty || saving} onClick={save}>
          {saving ? t("company.saving") : t("company.map.save")}
        </button>
      </div>
    </div>
  );
}
